import { useEffect, useMemo, useRef, useState } from "react";

const SelectField = ({
  label,
  name,
  value,
  onChange,
  options = [],
  placeholder = "Selecciona una opción",
  disabled = false,
  searchable = false,
  required = false,
  error = "",
  className = ""
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [nativeInvalid, setNativeInvalid] = useState(false);
  const wrapperRef = useRef(null);
  const nativeInputRef = useRef(null);


  const normalizedOptions = useMemo(() => {
    if (!Array.isArray(options)) return [];

    return options
      .filter(opt => opt !== undefined && opt !== null)
      .map(opt => {
        if (typeof opt === "string" || typeof opt === "number") {
          const text = String(opt);
          return { value: text, label: text };
        }

        if (typeof opt === "object") {
          const rawValue = opt.value ?? opt.label;
          const rawLabel = opt.label ?? opt.value;

          if (rawValue === undefined || rawValue === null) return null;

          return {
            value: String(rawValue),
            label: String(rawLabel ?? rawValue)
          };
        }

        return null;
      })
      .filter(Boolean);
  }, [options]);


  const filteredOptions = useMemo(() => {
    if (!searchable || !searchTerm) return normalizedOptions;

    return normalizedOptions.filter(opt =>
      opt.label.toLowerCase().includes(searchTerm.toLowerCase().trim())
    );
  }, [normalizedOptions, searchTerm, searchable]);

  const selectedLabel =
    normalizedOptions.find(opt => opt.value === value)?.label || "";

  const currentError = error || (nativeInvalid ? "Este campo es obligatorio" : "");


  const handleSelect = (opt) => {
    onChange({
      target: {
        name,
        value: opt.value
      }
    });

    setNativeInvalid(false);

    setOpen(false);
    setSearchTerm("");
  };


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) setSearchTerm("");
  }, [open]);

  useEffect(() => {
    if (!nativeInputRef.current) return;

    const isEmpty =
      value === null ||
      value === undefined ||
      (typeof value === "string" && value.trim() === "");

    nativeInputRef.current.setCustomValidity(required && !disabled && isEmpty ? "Este campo es obligatorio" : "");

    if (disabled || !isEmpty) {
      setNativeInvalid(false);
    }
  }, [disabled, required, value]);

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <label className="mb-1.5 block text-xs font-semibold uppercase text-brand-muted">
        {label}
        {required && <span className="ml-1 text-[#a34545]">*</span>}
      </label>

      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(prev => !prev)}
        className={`field-control w-full rounded-xl px-3 py-2.5 text-sm text-left transition-all ${currentError ? "border-[#a34545] ring-1 ring-[#a34545]" : ""
          } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
      >
        {selectedLabel || placeholder}
      </button>

      <input
        ref={nativeInputRef}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        name={name}
        value={value ?? ""}
        onChange={() => { }}
        onInvalid={() => setNativeInvalid(true)}
        disabled={disabled}
        required={required}
      />

      {currentError && (
        <p className="mt-1 text-xs text-[#8f3434]">{currentError}</p>
      )}

      {open && !disabled && (
        <div className="absolute z-20 mt-1 w-full rounded-xl border bg-white shadow-lg max-h-64 overflow-auto">

          {searchable && (
            <input
              className="w-full border-b px-3 py-2 text-sm outline-none"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          )}

          {filteredOptions.map((opt, index) => (
            <div
              key={`${name}-${opt.value}-${index}`}
              onClick={() => handleSelect(opt)}
              className="cursor-pointer px-3 py-2 text-sm hover:bg-brand-soft transition-colors"
            >
              {opt.label}
            </div>
          ))}

          {filteredOptions.length === 0 && (
            <div className="px-3 py-2 text-sm text-brand-muted">
              No hay resultados
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SelectField;